import { ClipboardCheck, LayoutDashboard, Package, Ticket, UsersRound, Wallet } from 'lucide-react'
import { AppPaths } from '@/config/app-paths'
import type { StaffNavContext } from '../staff-nav-context'
import { group, leaf, pushIfPresent, type StaffNavNode } from '../nav-node'
import { buildAccountGroup } from './account'

/**
 * Owner shell for a plain Administrator (not SystemAdministrator).
 * Dashboard, read-only payrolls, attendance, and user roles.
 * Gate pass approval stays with the assigned supervisor and is also listed here
 * so the owner admin can approve requests onboarding has passed on.
 */
export function buildThinAdminTree(ctx: StaffNavContext): StaffNavNode[] {
  const children: StaffNavNode[] = [
    leaf(
      'boss_dashboard',
      'Dashboard',
      AppPaths.bossDashboard,
      LayoutDashboard,
      ['dashboard', 'overview', 'money', 'billing', 'margin'],
      { activeWhenPrefix: false },
    ),
    leaf(
      'boss_payrolls',
      'Payrolls',
      AppPaths.bossPayrolls,
      Wallet,
      ['payroll', 'pay month', 'salary', 'runs'],
      { activeWhenPrefix: false },
    ),
    leaf(
      'boss_attendance',
      'Attendance summary',
      AppPaths.bossAttendance,
      ClipboardCheck,
      ['attendance', 'present', 'absent', 'today'],
      { activeWhenPrefix: false },
    ),
    leaf(
      'admin_users',
      'Users and roles',
      AppPaths.adminTeam,
      UsersRound,
      ['team', 'users', 'roles', 'staff', 'access', 'invite'],
      { activeWhenPrefix: true },
    ),
  ]

  children.push(
    leaf(
      'admin_gate_pass',
      'Gate Pass Approvals',
      AppPaths.adminGatePasses,
      Ticket,
      ['gate', 'pass', 'approval', 'approve', 'onboarding'],
      { activeWhenPrefix: true },
    ),
  )

  if (ctx.showStocksNav) {
    children.push(
      leaf(
        'stocks',
        'Stocks',
        AppPaths.adminStocks,
        Package,
        ['stocks', 'inventory', 'store', 'materials'],
        { activeWhenPrefix: true },
      ),
    )
  }

  const tree: StaffNavNode[] = []
  pushIfPresent(
    tree,
    group('overview', 'Overview', LayoutDashboard, ['overview', 'dashboard', 'payroll'], children),
  )
  pushIfPresent(tree, ctx.showAccountSection ? buildAccountGroup(ctx) : null)
  return tree
}
